import React, { useContext } from "react";
import {
  MdVpnKey,
  MdLibraryBooks,
  MdVerifiedUser,
  MdGroup,
  MdErrorOutline,
  MdSettings,
  MdFeed,
  MdTag,
  MdPageview,
} from "react-icons/md";
import { FaDatabase } from "react-icons/fa";
import { TiFlowMerge } from "react-icons/ti";
import { NavLink } from "react-router-dom";
import styled from "styled-components/macro";
import { AuthContext } from "../../../contexts/AuthContext";
import * as paths from "../../../common/paths";
import { useApi } from "../../../api/useApi";
import { rlrFeatureEnabled } from "../../../api/ruleQueries";
import { MdWorkspaces } from "react-icons/md";
import {
  generateContentName,
  generateId,
} from "../../../common/helpers/contentManagement";

const MenuIcon = styled.div`
  font-size: 1.2em;
  margin-right: ${(props) => (props.menuState ? "10px" : "")};
  color: ${(props) => props.theme.menuText};
  opacity: 0.5;
`;

const UserMenuButton = styled(NavLink)`
  display: flex;
  align-items: center;
  background: transparent;
  color: ${(props) => props.theme.menuText};
  padding: 1rem;
  padding-bottom: 0.8rem;
  padding-top: 0.8rem;
  margin-right: 0;
  margin-left: 0;
  border-left: 4px solid transparent;
  font-size: 1em;
  text-decoration: none;
  &:hover {
    cursor: pointer;
    background: ${(props) => props.theme.menuHoverActive};
  }
  &.active {
    background: ${(props) => props.theme.menuHoverActive};
    border-left: 4px solid ${(props) => props.theme.onSecondarySurface};
    ${MenuIcon} {
      opacity: 1;
    }
  }
`;

const NavList = styled.div`
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
`;

function UserMenuItem({ menuState }) {
  const { user } = useContext(AuthContext);
  const [{ data: rlrIsEnabledData }] = useApi(rlrFeatureEnabled);

  return (
    <NavList>
      <UserMenuButton
        id={generateId("link", "workspaces")}
        data-ai-content-name={generateContentName("workspaces mainnav")}
        to={paths.workSpaces()}
      >
        <MenuIcon menuState={menuState}>
          <MdWorkspaces />
        </MenuIcon>
        {menuState && "Workspaces"}
      </UserMenuButton>

      {user && user.role >= 2 && (
        <UserMenuButton
          id={generateId("link", "workgroups")}
          data-ai-content-name={generateContentName("workgroups mainnav")}
          to={paths.workGroups()}
        >
          <MenuIcon menuState={menuState}>
            <MdGroup />
          </MenuIcon>
          {menuState && "Workgroups"}
        </UserMenuButton>
      )}
      {user && user.role >= 2 && (
        <UserMenuButton
          id={generateId("link", "connections")}
          data-ai-content-name={generateContentName("connections mainnav")}
          to={paths.dataConnections()}
        >
          <MenuIcon menuState={menuState}>
            <MdVpnKey />
          </MenuIcon>
          {menuState && "Connections"}
        </UserMenuButton>
      )}

      <UserMenuButton
        id={generateId("link", "sources")}
        data-ai-content-name={generateContentName("sources mainnav")}
        to={paths.dataSources()}
      >
        <MenuIcon menuState={menuState}>
          <FaDatabase />
        </MenuIcon>
        {menuState && "Sources"}
      </UserMenuButton>

      <UserMenuButton
        //Check active by looking at pathname
        isActive={(match, location) => {
          let pathStrings = location.pathname.split("/");
          if (match) {
            return true;
          } else if (pathStrings[2] === "filters") {
            return true;
          } else {
            return false;
          }
        }}
        id={generateId("link", "policies")}
        data-ai-content-name={generateContentName("policies mainnav")}
        to={paths.dataQualityRules()}
      >
        <MenuIcon menuState={menuState}>
          <MdLibraryBooks />
        </MenuIcon>
        {menuState && "Policies"}
      </UserMenuButton>

      {rlrIsEnabledData?.rlrFeatureEnabled && (
        <UserMenuButton
          id={generateId("link", "dynamic-views")}
          data-ai-content-name={generateContentName("dynamic-views mainnav")}
          to={paths.dynamicViews()}
        >
          <MenuIcon menuState={menuState}>
            <MdPageview />
          </MenuIcon>
          {menuState && "Dynamic Views"}
        </UserMenuButton>
      )}

      {user && user.role === 3 && (
        <UserMenuButton
          id={generateId("link", "workflows")}
          data-ai-content-name={generateContentName("workflows mainnav")}
          to={paths.workflows()}
        >
          <MenuIcon menuState={menuState}>
            <TiFlowMerge />
          </MenuIcon>
          {menuState && "Workflows"}
        </UserMenuButton>
      )}

      <UserMenuButton
        id={generateId("link", "issues")}
        data-ai-content-name={generateContentName("issues mainnav")}
        to={paths.issues()}
      >
        <MenuIcon menuState={menuState}>
          <MdErrorOutline />
        </MenuIcon>
        {menuState && "Issues"}
      </UserMenuButton>

      <UserMenuButton
        id={generateId("link", "transformations")}
        data-ai-content-name={generateContentName("transformations mainnav")}
        to={paths.feeds()}
      >
        <MenuIcon menuState={menuState}>
          <MdFeed />
        </MenuIcon>
        {menuState && "Transformations"}
      </UserMenuButton>

      <UserMenuButton
        id={generateId("link", "tags")}
        data-ai-content-name={generateContentName("tags mainnav")}
        to={paths.tags()}
      >
        <MenuIcon menuState={menuState}>
          <MdTag />
        </MenuIcon>
        {menuState && "Tags"}
      </UserMenuButton>

      <UserMenuButton
        menuState={menuState}
        id={generateId("link", "settings")}
        data-ai-content-name={generateContentName("settings mainnav")}
        to={paths.settings()}
      >
        <MenuIcon menuState={menuState}>
          <MdSettings />
        </MenuIcon>
        {menuState && "Settings"}
      </UserMenuButton>

      {user && user.role === 3 && (
        <UserMenuButton
          id={generateId("link", "admin")}
          data-ai-content-name={generateContentName("admin mainnav")}
          to={paths.admin()}
        >
          <MenuIcon menuState={menuState}>
            <MdVerifiedUser />
          </MenuIcon>
          {menuState && "Admin"}
        </UserMenuButton>
      )}
    </NavList>
  );
}

export default UserMenuItem;
